/**
 * OnboardingAnalysisBeat — Phase 1 "Analysis" beat.
 *
 * Staged deep-research reveal: instant section read → user enrich → contact
 * enrich → calendar plan → profile portrait. Each stage assembles into ONE
 * OnboardingProfileCard that mounts as soon as selfName is deciphered and
 * fills in progressively as each stage completes.
 *
 * Past beats (isCurrent=false) render the card shell instantly with whatever
 * data is available — no fetches.
 */

import { useEffect, useRef, useState } from 'react';
import {
  TypedLines,
  AdvanceBubble,
  useRevealFallback,
  fetchObjects,
  pick,
  TOPIC_LABEL,
} from './onboarding-beats-shared';
import { refreshProfileSummary } from '@/lib/api/endpoints/profile-interview';
import { apiGet, apiPost, apiPatch } from '@/lib/api/client';
import { http } from '@/lib/http';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { apiFetch } from '@/lib/apiClient';
import type { BeatPayload, BeatContext, Bundle } from './onboarding-beats-shared';
import { OnboardingProfileCard } from './OnboardingProfileCard';
import type { OnboardingProfileCardPerson } from './OnboardingProfileCard';
import { isServiceContact } from './onboarding-personalization';
import { useWorkspacePlanTier } from '@/features/billing/hooks/useWorkspacePlanTier';
import { classifyTrips, inferLocation } from './onboarding-plans';
import { TravelReport, TripsTable } from './TravelReport';
import { ReservationsReport } from './ReservationsReport';
import { PersonaCard } from './PersonaCard';
import type { PersonaInputs } from './PersonaCard';
import type { ReservationRow } from './ReservationsReport';
import { CircleReport } from './CircleReport';
import type { CircleData } from './CircleReport';
import { SocialResultCard } from './result-cards/SocialResultCard';

// ── Intro copy ────────────────────────────────────────────────────────────────
const INTRO_LINES = [
  "Let me start with the people around you.",
];

// ── Discovery status shape (mirrors OnboardingBeats.tsx) ──────────────────────
interface DiscoveryJob {
  status: 'pending' | 'running' | 'done' | 'failed' | 'deferred';
  emailCount?: number;
  oldestDate?: string;
  oldestEventDate?: string;
  furthestEventDate?: string;
  contactCount?: number;
  innerCircleSize?: number;
  circle?: CircleData;
  peopleCandidates?: Array<{ name: string; email: string; sentCount: number; twoWay: boolean }>;
  sentTopics?: Array<{ topic: string; count: number }>;
  pastCount?: number;
  upcomingCount?: number;
  travelTrips?: Array<{ destination: string; dateRange: string; city?: string; lodging?: string; startISO?: string; endISO?: string }>;
  /** LLM read of the class of hotels the trips surfaced. */
  lodgingRead?: string;
  restaurantReservations?: Array<{ name: string; city?: string; whenISO: string; date: string }>;
  keyAppointments?: Array<{ name: string; city?: string; lastISO: string; lastDate: string; count: number }>;
  recurringMeetings?: Array<{ title: string; count: number }>;
}
interface DiscoveryStatus {
  jobs: { inbox: DiscoveryJob; sent: DiscoveryJob; calendar: DiscoveryJob; letter: DiscoveryJob };
  allDone: boolean;
}

// ── Investigation log ─────────────────────────────────────────────────────────
interface TripRow {
  destination: string;
  dateRange: string;
  city?: string;
  lodging?: string;
  startISO?: string;
  endISO?: string;
  planned?: boolean;
}
interface SocialReport {
  name: string;
  bio?: string | null;
  xProfile: { url: string; handle: string; followers?: string; highlights: Array<{ title: string; description: string; url: string; age?: string }> } | null;
  linkedIn: { url: string; headline: string } | null;
  recentMentions: Array<{ title: string; url: string; description: string; age?: string }>;
}

interface LogEntry {
  kind: 'stage' | 'item' | 'insight' | 'trips' | 'travel-report' | 'reservations' | 'persona' | 'circle-card' | 'social-card';
  text: string;
  trips?: TripRow[];
  reservations?: ReservationRow[];
  persona?: PersonaInputs;
  circleData?: CircleData | null;
  socialData?: SocialReport | null;
}

// ── IANA timezone → friendly region name ────────────────────────────────────
const IANA_TO_ZONE: Record<string, string> = {
  'America/Los_Angeles': 'Pacific',
  'America/Denver': 'Mountain',
  'America/Chicago': 'Central',
  'America/New_York': 'Eastern',
  'America/Phoenix': 'Mountain',
  'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii',
  'America/Toronto': 'Eastern',
  'America/Vancouver': 'Pacific',
  'Europe/London': 'GMT',
  'Europe/Paris': 'Central European',
  'Europe/Berlin': 'Central European',
};

function ianaToFriendly(iana: string): string {
  if (IANA_TO_ZONE[iana]) return IANA_TO_ZONE[iana];
  // Fall back to the city part of the IANA string
  const city = iana.split('/').pop()?.replace(/_/g, ' ') ?? iana;
  return city;
}

// Extract dominant timezone from event objects
function dominantTimezone(events: Array<Record<string, unknown>>): string | null {
  const counts = new Map<string, number>();
  for (const e of events) {
    const tz = String(pick(e, 'timezone') ?? '').trim();
    if (tz.includes('/')) counts.set(tz, (counts.get(tz) ?? 0) + 1);
  }
  let topTz = '';
  let topN = 0;
  for (const [tz, n] of counts) {
    if (n > topN) { topTz = tz; topN = n; }
  }
  return topN >= 2 ? topTz : null;
}

// Extract airport codes from travel trips (and event titles as backup)
const AIRPORT_RE = /\b([A-Z]{3})\b/g;
const CITY_WORDS_RE = /\b(?:airport|flight|fly|flying|depart|arrive|terminal|gate|international|domestic)\b/i;
// All-caps words that match the 3-letter regex but aren't IATA codes — "LOS"
// from "Flight to LOS ANGELES" once out-counted LAX as the home airport.
// (Mirrors NON_AIRPORT_TRIGRAMS in apps/api calendar-scan.ts.)
const NON_AIRPORT_TRIGRAMS = new Set([
  'LOS', 'SAN', 'NEW', 'LAS', 'THE', 'AND', 'FOR', 'OUT', 'OFF', 'VIA',
  'OOO', 'USA', 'TBD', 'ETA', 'ETD', 'PST', 'EST', 'GMT', 'UTC',
]);

function dominantAirport(
  trips: Array<{ destination: string; dateRange: string }>,
  events: Array<Record<string, unknown>>,
): string | null {
  const counts = new Map<string, number>();

  const countCodes = (text: string) => {
    let m: RegExpExecArray | null;
    AIRPORT_RE.lastIndex = 0;
    while ((m = AIRPORT_RE.exec(text)) !== null) {
      if (NON_AIRPORT_TRIGRAMS.has(m[1])) continue;
      counts.set(m[1], (counts.get(m[1]) ?? 0) + 1);
    }
  };

  // From trip destinations
  for (const t of trips) countCodes(t.destination ?? '');

  // From event titles that look flight-related
  for (const e of events) {
    const title = String(pick(e, 'title') ?? '').trim();
    if (CITY_WORDS_RE.test(title)) countCodes(title);
  }

  let topCode = '';
  let topN = 0;
  for (const [code, n] of counts) {
    if (n > topN) { topCode = code; topN = n; }
  }
  return topN >= 2 ? topCode : null;
}

// ── helpers ───────────────────────────────────────────────────────────────────
function paragraphs(text: string): string[] {
  return text.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
}

// ── Component ─────────────────────────────────────────────────────────────────
export function OnboardingAnalysisBeat({
  payload,
  ctx,
}: {
  payload: BeatPayload;
  ctx: BeatContext;
}) {
  const bundle = payload.bundle as Bundle | undefined;

  // Intro typed-lines gate.
  const [introDone, setIntroDone] = useState(!ctx.isCurrent);

  // People section — enriched progressively with role+bio.
  const [people, setPeople] = useState<OnboardingProfileCardPerson[]>([]);
  const [bio, setBio] = useState<string[] | null>(null);

  // Investigation log — stages, items, insights.
  const [log, setLog] = useState<LogEntry[]>([]);

  // Click-through pacing: each reveal section waits for an explicit Next.
  const [awaitingNext, setAwaitingNext] = useState(false);
  const nextResolverRef = useRef<(() => void) | null>(null);

  // Free tier sees the deeper-history teaser under the travel card; the
  // tier rides a ref so the async log effect reads the latest value.
  const planTier = useWorkspacePlanTier();
  const planTierRef = useRef(planTier);
  planTierRef.current = planTier;

  // True once all stages have settled (success or skipped).
  const [finished, setFinished] = useState(!ctx.isCurrent);

  // The deciphered identity (self-contact display name).
  const [selfName, setSelfName] = useState('');
  const [selfEmail, setSelfEmail] = useState('');

  // Data for the profile card (populated progressively as stages complete).
  const [profileLinks, setProfileLinks] = useState<{ web?: string; linkedin?: string; x?: string } | undefined>();
  const [identityLine, setIdentityLine] = useState<string | undefined>();
  const [hasPublicPresence, setHasPublicPresence] = useState<boolean | undefined>();
  const [travelTrips, setTravelTrips] = useState<Array<{ destination: string; dateRange: string }> | undefined>();
  const [recurringMeetings, setRecurringMeetings] = useState<string[] | undefined>();
  const [upcomingCount, setUpcomingCount] = useState<number | undefined>();
  const [topics, setTopics] = useState<string[] | undefined>();
  const [emailsRead, setEmailsRead] = useState<number | undefined>();
  const [eventsRead, setEventsRead] = useState<number | undefined>();
  const [sentCount, setSentCount] = useState<number | undefined>();

  // Safety net: reveal after 7s if animation never fires.
  useRevealFallback(ctx.isCurrent && !finished, () => setFinished(true));

  // Single ranRef guard — effect runs once per live beat.
  const ranRef = useRef(false);
  // Social report pre-fetched early so it's ready by the time the social card renders.
  const socialFetchRef = useRef<Promise<SocialReport | null> | null>(null);

  useEffect(() => {
    if (!ctx.isCurrent || !introDone) return;
    if (ranRef.current) return;
    ranRef.current = true;

    let alive = true;

    function pushLog(kind: LogEntry['kind'], text: string) {
      if (!alive) return;
      setLog((prev) => [...prev, { kind, text }]);
    }

    // Pause until the user clicks Next (live beats only — replayed past
    // beats never gate).
    async function waitForNext() {
      if (!alive || !ctx.isCurrent) return;
      setAwaitingNext(true);
      await new Promise<void>((res) => { nextResolverRef.current = res; });
    }

    async function run() {
      if (!bundle) { if (alive) setFinished(true); return; }

      // ── Fetch discovery status for calendar/sent metadata ─────────────
      let discoveryStatus: DiscoveryStatus | null = null;
      try {
        const wsId = ctx.workspaceId ?? getActiveWorkspaceId();
        discoveryStatus = await apiFetch<DiscoveryStatus>(
          `/workspaces/${wsId}/onboarding/discovery-status`,
          { authToken: ctx.token ?? undefined },
        );
        if (alive && discoveryStatus) {
          const sent = discoveryStatus.jobs.sent;
          const cal = discoveryStatus.jobs.calendar;
          const inbox = discoveryStatus.jobs.inbox;

          if (inbox?.emailCount) setEmailsRead(inbox.emailCount);
          const total = (cal?.pastCount ?? 0) + (cal?.upcomingCount ?? 0);
          if (total > 0) setEventsRead(total);
          if (cal?.upcomingCount) setUpcomingCount(cal.upcomingCount);
          if (cal?.travelTrips?.length) setTravelTrips(cal.travelTrips);
          if (cal?.recurringMeetings?.length) {
            setRecurringMeetings(cal.recurringMeetings.slice(0, 3).map((m) => m.title).filter(Boolean));
          }
          if (sent?.sentTopics?.length) {
            const mapped = sent.sentTopics.map((t) => TOPIC_LABEL[t.topic] ?? t.topic).slice(0, 4);
            if (mapped.length > 0) setTopics(mapped);
          }
          if (sent?.contactCount) setSentCount(sent.contactCount);

          // ── Pre-fetch social report in background while other cards render ──
          const wsId = ctx.workspaceId ?? getActiveWorkspaceId();
          socialFetchRef.current = apiFetch<{ socialReport: SocialReport | null }>(
            `/workspaces/${wsId}/social-report`,
            { authToken: ctx.token ?? undefined },
          ).then((d) => d?.socialReport ?? null).catch(() => null);

          // ── 1. Circle — people around you ──────────────────────────────────
          const circleData = sent?.circle ?? null;
          const hasStrongCircle = (circleData?.regulars ?? 0) >= 3;

          const allTrips = cal?.travelTrips ?? [];

          if (hasStrongCircle && circleData) {
            if (alive) setLog((prev) => [...prev, { kind: 'circle-card', text: '', circleData }]);
            await waitForNext();
          }

          // ── 2. Travel ──────────────────────────────────────────────────────
          if (allTrips.length > 0) {
            const bridgeLine = hasStrongCircle
              ? 'You move around too — here\'s where.'
              : `${total > 0 ? `I read ${total.toLocaleString()} events. ` : ''}The clearest pattern is how you move — your last twelve months, and what\'s ahead:`;
            pushLog('insight', bridgeLine);
            const { recent, upcoming } = classifyTrips(allTrips);
            const rows: TripRow[] = [
              ...recent.slice(-20),
              ...upcoming.slice(0, 8).map((t) => ({ ...t, planned: true })),
            ];
            if (alive && rows.length > 0) setLog((prev) => [...prev, { kind: 'travel-report', text: '', trips: rows }]);
            if (cal?.lodgingRead) pushLog('insight', cal.lodgingRead);
            if (planTierRef.current !== 'pro' && planTierRef.current !== 'team') {
              pushLog('item', 'On Pro I can read years of history, not just one — the deeper I see, the better I can recommend places that fit both your taste and your budget.');
            }
            await waitForNext();
          }

          // ── 3. Food — the tables you keep ─────────────────────────────────
          const calRes = cal?.restaurantReservations ?? [];
          const mailRes = inbox?.restaurantReservations ?? [];
          const calKeys = new Set(calRes.map((r) => `${r.name.toLowerCase()}|${new Date(r.whenISO).toDateString()}`));
          const merged: ReservationRow[] = [
            ...calRes,
            ...mailRes.filter((r) => !calKeys.has(`${r.name.toLowerCase()}|${new Date(r.whenISO).toDateString()}`)),
          ];
          if (merged.length > 0) {
            pushLog('stage', 'And the tables you keep.');
            if (alive) setLog((prev) => [...prev, { kind: 'reservations', text: '', reservations: merged }]);
            await waitForNext();
          }

          // ── 4. Social — outside view ──────────────────────────────────────
          const socialData = await (socialFetchRef.current ?? Promise.resolve(null));
          if (socialData) {
            pushLog('insight', "That\'s your world from the inside. Here\'s how you show up on the web.");
            if (alive) setLog((prev) => [...prev, { kind: 'social-card', text: '', socialData }]);
            await waitForNext();
          }

          // ── 5. Profile — put it all together ──────────────────────────────
          {
            const { recent: pr, upcoming: pu } = classifyTrips(allTrips);
            const personaTrips: TripRow[] = [...pr, ...pu.map((t) => ({ ...t, planned: true }))];
            const personaInputs: PersonaInputs = {
              trips: personaTrips,
              reservations: merged,
              lodgings: allTrips.map((t) => t.lodging).filter(Boolean) as string[],
              sentTopics: sent?.sentTopics ?? [],
              innerCircleSize: sent?.innerCircleSize,
              contactCount: sent?.circle?.regulars ?? sent?.contactCount,
            };
            if (personaTrips.length + merged.length > 0) {
              pushLog('stage', 'Put together — here\'s your card:');
              if (alive) setLog((prev) => [...prev, { kind: 'persona', text: '', persona: personaInputs }]);
              await waitForNext();
            }
          }

          // Show circle last if it was too thin to lead
          if (!hasStrongCircle && circleData && (circleData.closest?.length ?? 0) > 0) {
            if (alive) setLog((prev) => [...prev, { kind: 'circle-card', text: '', circleData }]);
          }

          // ── Log: Reading your email and calendar… ──────────────────
          // Each line carries the sample size AND how far it reaches, so the
          // user can see exactly what the read covered.
          pushLog('stage', 'Reading your email and calendar…');
          const mdy = (iso?: string) => {
            const d = iso ? new Date(iso) : null;
            return d && !Number.isNaN(d.getTime())
              ? `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
              : '';
          };
          try {
            if (total > 0) {
              const from = mdy(cal?.oldestEventDate);
              const to = mdy(cal?.furthestEventDate);
              const span = from && to ? `, ${from} → ${to}` : '';
              pushLog('item', `Calendar — ${total} events${span}`);
            }
            if (sent?.contactCount || sent?.emailCount) {
              const back = mdy(sent?.oldestDate);
              const sample = sent?.emailCount
                ? `${sent.emailCount} messages${back ? ` going back to ${back}` : ''}`
                : '';
              const people = sent?.contactCount ? `${sent.contactCount} people you write to` : '';
              pushLog('item', `Sent — ${[sample, people].filter(Boolean).join(' · ')}`);
            }
            if (inbox?.emailCount) {
              const back = mdy(inbox?.oldestDate);
              pushLog('item', `Inbox — ${inbox.emailCount} messages${back ? ` going back to ${back}` : ''}`);
            }
          } catch { /* best-effort */ }
        }
      } catch { /* non-fatal, continue */ }

      // ── Decipher the user's identity from their own mailbox ────────────
      if (!alive) return;
      const selfEmails = new Set<string>();
      const loginEmail = String(ctx.userEmail ?? '').trim().toLowerCase();
      if (loginEmail) selfEmails.add(loginEmail);
      let mailboxEmail = '';
      try {
        const wsId = ctx.workspaceId ?? getActiveWorkspaceId();
        const status = await http.get<{ accounts?: Array<{ id?: string; email?: string; tokenValid?: boolean }> }>(
          `/workspaces/${wsId}/integrations/email/status`,
          { authToken: ctx.token ?? null, headers: { 'x-workspace-id': wsId } },
        );
        for (const a of status.data.accounts ?? []) {
          const e = String(a.email ?? '').trim().toLowerCase();
          if (e && !String(a.id ?? '').startsWith('demo-integration-')) {
            selfEmails.add(e);
            if (!mailboxEmail) mailboxEmail = e;
          }
        }
      } catch { /* fall back to the login email alone */ }

      const allContacts = await fetchObjects(ctx, 'contact', 100);
      const realContacts = allContacts.filter(
        (c) => !String(pick(c, 'source') ?? '').startsWith('demo-'),
      );
      const isSelf = (c: Record<string, unknown>) =>
        selfEmails.has(String(pick(c, 'email') ?? '').trim().toLowerCase());
      const selfContact = realContacts.find(isSelf);
      const decipheredName =
        String(selfContact ? (pick(selfContact, 'name') ?? pick(selfContact, 'title') ?? '') : '').trim() ||
        ctx.userName?.trim() ||
        bundle.name;
      if (alive) {
        setSelfName(decipheredName);
        if (mailboxEmail) setSelfEmail(mailboxEmail);
      }

      // ── Location from the calendar alone (events reused for plans hints) ─
      if (!alive) return;
      let locationGuess: string | null = null;
      let eventsCache: Array<Record<string, unknown>> = [];
      try {
        eventsCache = ((await fetchObjects(ctx, 'event', 400)) as Array<Record<string, unknown>>)
          // The reveal reads the USER's world only — no demo-seeded events or
          // old onboarding event-gen copies in timezone/airport inference.
          .filter((e) => {
            const src = String(pick(e, 'source') ?? '');
            return !src.startsWith('demo') && src !== 'onboarding-discovery';
          });
        locationGuess = inferLocation(eventsCache);
      } catch { /* no location signal — skip */ }

      // ── Log: Getting my bearings… ───────────────────────────────────────
      // Timezone + home airport. The trip list itself already opened the log.
      pushLog('stage', 'Getting my bearings…');
      try {
        const tz = dominantTimezone(eventsCache);
        if (tz) {
          const zone = ianaToFriendly(tz);
          pushLog('insight', `You're in the ${zone} time zone.`);
        }
        const currentTravelTrips = discoveryStatus?.jobs?.calendar?.travelTrips ?? [];
        const airport = dominantAirport(currentTravelTrips, eventsCache);
        if (airport) {
          pushLog('insight', `Most of your flights leave from ${airport}.`);
        }
      } catch { /* best-effort — skip on any parse failure */ }

      // ── Stage A0: research the USER, seeded with every clue we have ────
      if (!alive) return;
      // Track links locally so Stage C can reference them without stale closure
      let localProfileLinks: { web?: string; linkedin?: string; x?: string } = {};
      try {
        const name = decipheredName;
        const email = mailboxEmail || ctx.userEmail || undefined;
        const knownData: Record<string, string> = {};
        if (email) knownData.email = email;
        const topicWords = (discoveryStatus?.jobs?.sent?.sentTopics ?? [])
          .slice(0, 2)
          .map((t) => String(t.topic).split('/')[0])
          .filter(Boolean);
        const hints = [...selfEmails, ...topicWords, locationGuess ?? '']
          .filter(Boolean)
          .join(' ');

        const { data: enrichRes } = await apiPost<{
          confidence: string;
          fields?: Record<string, unknown>;
          rawSummary?: string;
        }>('/contacts/enrich', { name, ...(hints ? { hints } : {}), knownData });

        if (alive && enrichRes.confidence !== 'none') {
          if (enrichRes.rawSummary) {
            setBio(paragraphs(enrichRes.rawSummary));
          }
          // Extract fields for profile card
          const fields = enrichRes.fields ?? {};
          const parts: string[] = [];
          const title = String(fields.contactTitle ?? '').trim();
          const company = String(fields.company ?? bundle.company ?? '').trim();
          // The calendar knows where you LIVE; the web bio may lag.
          const location = locationGuess ?? String(fields.location ?? '').trim();
          if (title) parts.push(title);
          if (company && company !== title) parts.push(company);
          if (location) parts.push(location);
          if (parts.length > 0 && alive) setIdentityLine(parts.join(' · '));

          const linksObj: { web?: string; linkedin?: string; x?: string } = {};
          if (typeof fields.linkedinUrl === 'string' && fields.linkedinUrl) {
            linksObj.linkedin = fields.linkedinUrl;
          }
          const xHandle = fields.xHandle as { handle?: string } | null | undefined;
          if (xHandle?.handle) {
            linksObj.x = `https://x.com/${xHandle.handle}`;
          }
          if (Object.keys(linksObj).length > 0) {
            localProfileLinks = linksObj;
            if (alive) setProfileLinks(linksObj);
          }
          if (alive) {
            setHasPublicPresence(enrichRes.confidence === 'high');
          }

          // Best-effort persist into identity dimension
          if (enrichRes.rawSummary) {
            try {
              const wsId = ctx.workspaceId ?? getActiveWorkspaceId();
              const dims = await http.get<{ items?: Array<{ dimension: string; content?: string }> }>(
                `/workspaces/${wsId}/profile-dimensions`,
                { authToken: ctx.token ?? null, headers: { 'x-workspace-id': wsId } },
              );
              const identity = dims.data.items?.find((d) => d.dimension === 'identity');
              if (!identity?.content?.trim()) {
                await http.put<unknown>(
                  `/workspaces/${wsId}/profile-dimensions/identity`,
                  { content: enrichRes.rawSummary, source: 'inferred' },
                  { authToken: ctx.token ?? null, headers: { 'x-workspace-id': wsId } },
                );
              }
            } catch { /* non-fatal */ }
          }
        }
      } catch {
        // Stage A0 failure → skip enrich fields, continue
      }
      // Even without web research, the calendar-derived location belongs on
      // the card's identity line.
      if (alive && locationGuess) setIdentityLine((prev) => prev ?? locationGuess ?? undefined);

      // ── Stage B: enrich top PEOPLE (never the user, never vendors) ─────
      if (!alive) return;
      pushLog('stage', 'Researching the people in your world…');

      const nameLc = decipheredName.toLowerCase();
      const isOtherHuman = (name: string, email: string) =>
        !!name &&
        name.toLowerCase() !== nameLc &&
        !selfEmails.has(email.toLowerCase()) &&
        !isServiceContact(name, email);

      const byEmail = new Map<string, Record<string, unknown>>();
      for (const c of realContacts) {
        const e = String(pick(c, 'email') ?? '').trim().toLowerCase();
        if (e && !byEmail.has(e)) byEmail.set(e, c);
      }

      const candidates = (discoveryStatus?.jobs?.sent?.peopleCandidates ?? [])
        .filter((p) => isOtherHuman(String(p.name ?? '').trim(), String(p.email ?? '').trim()))
        .sort((a, b) => Number(b.twoWay) - Number(a.twoWay) || (b.sentCount ?? 0) - (a.sentCount ?? 0));

      const seenNames = new Set<string>();
      const top5: Array<Record<string, unknown>> = [];
      for (const p of candidates) {
        const n = String(p.name ?? '').trim().toLowerCase();
        if (!n || seenNames.has(n)) continue;
        seenNames.add(n);
        top5.push(byEmail.get(String(p.email ?? '').trim().toLowerCase()) ?? { name: p.name, email: p.email });
        if (top5.length >= 5) break;
      }
      // Fallback: no sent-mail candidates → filtered contact ranking.
      if (top5.length === 0) {
        const others = realContacts.filter((c) =>
          isOtherHuman(
            String(pick(c, 'name') ?? pick(c, 'title') ?? '').trim(),
            String(pick(c, 'email') ?? '').trim(),
          ),
        );
        others.sort(
          (a, b) => Number(pick(b, 'emailCount') ?? 0) - Number(pick(a, 'emailCount') ?? 0),
        );
        for (const c of others) {
          const n = String(pick(c, 'name') ?? pick(c, 'title') ?? '').trim().toLowerCase();
          if (!n || seenNames.has(n)) continue;
          seenNames.add(n);
          top5.push(c);
          if (top5.length >= 5) break;
        }
      }

      let thinCount = 0;

      for (const contact of top5) {
        if (!alive) break;
        const contactName = String(pick(contact, 'name') ?? pick(contact, 'title') ?? '').trim();
        const contactEmail = String(pick(contact, 'email') ?? '').trim() || undefined;
        const contactCompany = String(pick(contact, 'company') ?? '').trim() || undefined;
        const contactId = String(contact.id ?? '');

        if (!contactName) continue;

        const contactRole = String(pick(contact, 'role') ?? pick(contact, 'contactTitle') ?? '').trim();
        const who = [contactRole, contactCompany].filter(Boolean).join(', ');
        const nEmails = Number(pick(contact, 'emailCount') ?? 0);
        const localNote = [
          who || contactEmail || '',
          nEmails > 0 ? `${nEmails} emails between you` : '',
        ].filter(Boolean).join(' · ');

        let enrichedBio = '';
        let enrichedRole = who;
        let enrichConfidence = 'none';
        let etag: string | undefined;
        try {
          const detail = contactId
            ? await apiGet<Record<string, unknown>>(`/contacts/${contactId}`)
            : null;
          etag = detail?.etag;
          enrichedBio = String(
            (detail && (pick(detail.data, 'enrichmentSummary') ?? pick(detail.data, 'summary'))) ?? '',
          ).trim();
        } catch { /* detail fetch failed — fall through to enrich */ }

        if (!enrichedBio) {
          try {
            const knownData: Record<string, string> = {};
            if (contactEmail) knownData.email = contactEmail;
            if (contactCompany) knownData.company = contactCompany;

            const { data: enriched } = await apiPost<{
              confidence: string;
              fields?: Record<string, unknown>;
              rawSummary?: string;
              enrichmentSkills?: string[];
            }>('/contacts/enrich', { name: contactName, knownData });

            enrichConfidence = enriched.confidence ?? 'none';

            if (enriched.rawSummary && enriched.confidence !== 'none') {
              enrichedBio = enriched.rawSummary;

              // Extract role from enrich fields when contact record lacks one
              if (!enrichedRole && enriched.fields) {
                const ft = String(enriched.fields.contactTitle ?? '').trim();
                const fc = String(enriched.fields.company ?? '').trim();
                enrichedRole = [ft, fc].filter(Boolean).join(', ');
              }

              if (contactId && etag) {
                try {
                  await apiPatch<Record<string, unknown>>(
                    `/contacts/${contactId}`,
                    {
                      enrichmentSummary: enriched.rawSummary,
                      enrichmentConfidence: enriched.confidence,
                      enrichmentAttemptedAt: new Date().toISOString(),
                      ...(enriched.enrichmentSkills?.length
                        ? { enrichmentSkills: enriched.enrichmentSkills }
                        : {}),
                    },
                    etag,
                  );
                } catch { /* persist failure is non-fatal */ }
              }
            } else {
              // No usable data
              thinCount++;
            }
          } catch { /* enrich failure → fall back to local knowledge; count as thin */ thinCount++; }
        } else {
          // We had a cached enrichment summary — treat as success
          enrichConfidence = 'cached';
        }

        if (alive) {
          // role: enriched title+company, or the local "who" fallback, or the localNote
          const finalRole = enrichedRole || localNote;
          setPeople((prev) => [
            ...prev,
            {
              name: contactName,
              role: finalRole || undefined,
              bio: enrichedBio || undefined,
            },
          ]);

          // Log the person with their role if enrichment succeeded
          try {
            const hadRole = !!(enrichedRole || who);
            if (enrichConfidence !== 'none' && enrichConfidence !== 'cached') {
              const logRole = enrichedRole || who;
              if (logRole) {
                pushLog('item', `${contactName} — ${logRole}`);
              } else {
                pushLog('item', contactName);
              }
            } else if (enrichedBio) {
              // cached hit
              const logRole = finalRole;
              if (logRole) {
                pushLog('item', `${contactName} — ${logRole}`);
              } else {
                pushLog('item', contactName);
              }
            } else if (hadRole) {
              pushLog('item', `${contactName} — ${finalRole}`);
            } else {
              pushLog('item', contactName);
            }
          } catch { /* best-effort */ }
        }
      }

      // Thin contacts summary
      if (alive && thinCount > 0) {
        const them = thinCount === 1 ? 'it' : 'them';
        const s = thinCount === 1 ? '' : 's';
        pushLog('insight', `${thinCount} contact${s} look thin — holding ${them} for review.`);
      }

      // ── Stage C: calendar plans (discovery-status already fetched above) ──
      if (!alive) return;
      // Topics / handles
      try {
        const currentTopics = discoveryStatus?.jobs?.sent?.sentTopics ?? [];
        const mappedTopics = currentTopics.map((t) => TOPIC_LABEL[t.topic] ?? t.topic).slice(0, 4);
        const hasHandles = !!(localProfileLinks.linkedin || localProfileLinks.x);

        if (mappedTopics.length > 0 || hasHandles) {
          pushLog('stage', 'Reading what you\'re working on…');
          if (mappedTopics.length > 0) {
            pushLog('insight', `Your mail clusters on ${mappedTopics.join(', ').toLowerCase()}.`);
          }
          // Social handles — one line per network, each with its found link
          // (a bare "LinkedIn · X" line read like an unfinished thought).
          const shortLink = (url: string) =>
            url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '');
          if (localProfileLinks.linkedin) {
            pushLog('item', `LinkedIn — found you at ${shortLink(localProfileLinks.linkedin)}`);
          }
          if (localProfileLinks.x) {
            const handle = localProfileLinks.x.match(/(?:x|twitter)\.com\/@?([A-Za-z0-9_]+)/)?.[1];
            pushLog('item', `X — ${handle ? `you post as @${handle}` : `found you at ${shortLink(localProfileLinks.x)}`}`);
          }
        }
      } catch { /* best-effort */ }

      try {
        await fetchObjects(ctx, 'event', 400);
        // Calendar data comes primarily from discovery-status (already set above).
      } catch { /* skip */ }

      // ── Stage D: portrait ─────────────────────────────────────────────
      if (!alive) return;
      pushLog('stage', 'Assembling your profile…');
      try {
        const workspaceId = ctx.workspaceId ?? getActiveWorkspaceId();
        const { profile } = await refreshProfileSummary(ctx.token ?? null, workspaceId);
        if (alive && profile && profile.trim().length > 0) {
          setBio(paragraphs(profile));
        }
      } catch { /* Stage D failure → skip portrait */ }

      if (alive) {
        setFinished(true);
      }
    }

    void run();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introDone, ctx.isCurrent]);

  if (!bundle) return null;

  // The card mounts as soon as selfName is deciphered (or if not current, immediately).
  const showCard = !!(selfName || !ctx.isCurrent);

  // Determine if profile card has any meaningful data at all (for degraded path).
  const hasProfileData = !!(selfName || identityLine || profileLinks || travelTrips?.length || recurringMeetings?.length || topics?.length || bio?.length);

  // Find the last stage index for the spinner
  const lastStageIdx = log.reduceRight((found, entry, i) => found === -1 && entry.kind === 'stage' ? i : found, -1);

  return (
    <>
      {/* Intro typed paragraph */}
      <TypedLines
        active={ctx.isCurrent}
        className="obr-assess-intro"
        lines={INTRO_LINES}
        onComplete={() => setIntroDone(true)}
      />

      {/* Investigation log — the detective trail, visible during and after assembly */}
      {ctx.isCurrent && log.length > 0 && (
        <div className="oal-log">
          {log.map((entry, i) => {
            const isLastStage = entry.kind === 'stage' && i === lastStageIdx;
            return (
              <div key={i} className={`oal-row oal-row--${entry.kind} oal-fade`}>
                {entry.kind === 'stage' && (
                  <span className="oal-stage">
                    {entry.text}
                    {isLastStage && !finished && (
                      <span className="oal-stage-spin" />
                    )}
                  </span>
                )}
                {entry.kind === 'item' && (
                  <span className="oal-item">
                    <span className="oal-item-text">{entry.text}</span>
                    <span className="oal-check">✓</span>
                  </span>
                )}
                {entry.kind === 'insight' && (
                  <span className="oal-insight">
                    <span className="oal-dash">—</span>
                    <span className="oal-insight-text">{entry.text}</span>
                  </span>
                )}
                {entry.kind === 'trips' && entry.trips && <TripsTable trips={entry.trips} />}
                {entry.kind === 'travel-report' && entry.trips && <TravelReport trips={entry.trips} />}
                {entry.kind === 'reservations' && entry.reservations && <ReservationsReport reservations={entry.reservations} />}
                {entry.kind === 'persona' && entry.persona && <PersonaCard inputs={entry.persona} />}
                {entry.kind === 'circle-card' && <CircleReport circle={entry.circleData!} />}
                {entry.kind === 'social-card' && <SocialResultCard initialData={entry.socialData} />}
              </div>
            );
          })}
          {awaitingNext && (
            <button
              type="button"
              className="oa-next-btn obr-fade"
              onClick={() => {
                setAwaitingNext(false);
                nextResolverRef.current?.();
                nextResolverRef.current = null;
              }}
            >
              Next
            </button>
          )}
        </div>
      )}

      {/* ONE assembling reveal card — mounts as soon as selfName is known */}
      {showCard && hasProfileData && (
        <OnboardingProfileCard
          name={selfName || bundle.name}
          email={selfEmail || ctx.userEmail || undefined}
          identityLine={identityLine}
          links={profileLinks}
          hasPublicPresence={hasPublicPresence}
          travelUpcoming={travelTrips ? classifyTrips(travelTrips).upcoming : undefined}
          travelRecent={travelTrips ? classifyTrips(travelTrips).recent : undefined}
          recurring={recurringMeetings}
          upcomingCount={upcomingCount}
          topics={topics}
          bio={bio ?? undefined}
          people={people.length > 0 ? people : undefined}
          provenance={{
            calendar: eventsRead,
            sent: sentCount,
            inbox: emailsRead,
          }}
          assembling={!finished}
          emailsRead={emailsRead}
          eventsRead={eventsRead}
          onConfirm={() => {
            const currentTravelTrips = travelTrips ?? [];
            const upcomingTrip = currentTravelTrips.length > 0 ? classifyTrips(currentTravelTrips).upcoming[0] : undefined;
            const topFirstName = people[0]?.name?.split(' ')[0];
            ctx.advance('commands', {
              bundle: payload.bundle
                ? { ...payload.bundle, name: selfName || payload.bundle.name }
                : payload.bundle,
              analysis: {
                upcomingCount,
                topFirst: topFirstName,
                recurringFirst: recurringMeetings?.[0],
                nextTripDestination: upcomingTrip?.destination,
                nextTripDate: upcomingTrip?.dateRange,
              },
            });
          }}
          onFix={() => {/* InterviewPanel handled inside card */}}
        />
      )}

      {/* Fallback AdvanceBubble — shown only in degraded path (no selfName, no data) */}
      {finished && !hasProfileData && (
        <AdvanceBubble
          label="Show me what you can do"
          isCurrent={ctx.isCurrent}
          onAdvance={() => {
            const currentTravelTrips = travelTrips ?? [];
            const upcomingTrip = currentTravelTrips.length > 0 ? classifyTrips(currentTravelTrips).upcoming[0] : undefined;
            const topFirstName = people[0]?.name?.split(' ')[0];
            ctx.advance('commands', {
              bundle: payload.bundle
                ? { ...payload.bundle, name: selfName || payload.bundle.name }
                : payload.bundle,
              analysis: {
                upcomingCount,
                topFirst: topFirstName,
                recurringFirst: recurringMeetings?.[0],
                nextTripDestination: upcomingTrip?.destination,
                nextTripDate: upcomingTrip?.dateRange,
              },
            });
          }}
        />
      )}
    </>
  );
}
